import type { WorkingMemoryV1 } from '@tabbio/types';

export type WorkingMemorySaveErrorKey =
  | 'settings.memoryGraph.advancedInvalidJsonError'
  | 'settings.memoryGraph.advancedInvalidStructureError'
  | 'settings.memoryGraph.advancedValidationError'
  | 'settings.memoryGraph.advancedNetworkError'
  | 'settings.memoryGraph.advancedSaveError';

export class WorkingMemoryDraftError extends Error {
  constructor(readonly code: 'INVALID_JSON' | 'INVALID_STRUCTURE' | 'INVALID_FIELDS') {
    super(code);
    this.name = 'WorkingMemoryDraftError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNullableString(value: unknown) {
  return value === null || typeof value === 'string';
}

function hasValidKnownFields(value: Record<string, unknown>) {
  const nullableStringFields = [
    'name',
    'currentTitle',
    'targetRole',
    'language',
    'lastApprovedCvId',
    'plan',
  ] as const;

  for (const field of nullableStringFields) {
    if (field in value && value[field] !== undefined && !isNullableString(value[field])) {
      return false;
    }
  }

  if (
    'workPreferences' in value &&
    value.workPreferences !== undefined &&
    !isRecord(value.workPreferences)
  ) {
    return false;
  }

  if (
    'activeGoals' in value &&
    value.activeGoals !== undefined &&
    (!Array.isArray(value.activeGoals) ||
      value.activeGoals.some((goal) => typeof goal !== 'string'))
  ) {
    return false;
  }

  if (
    'salaryExpectation' in value &&
    value.salaryExpectation !== undefined &&
    value.salaryExpectation !== null
  ) {
    if (!isRecord(value.salaryExpectation)) return false;

    const { amount, currency, period } = value.salaryExpectation;
    if (amount !== undefined && amount !== null && typeof amount !== 'number') return false;
    if (currency !== undefined && !isNullableString(currency)) return false;
    if (period !== undefined && !isNullableString(period)) return false;
  }

  return true;
}

/**
 * Parse and minimally validate the Advanced memory editor before sending it.
 * The API remains authoritative; this only turns common editing mistakes into
 * immediate, actionable feedback instead of an opaque failed request.
 */
export function parseWorkingMemoryDraft(draft: string): Partial<WorkingMemoryV1> {
  let parsed: unknown;

  try {
    parsed = JSON.parse(draft);
  } catch {
    throw new WorkingMemoryDraftError('INVALID_JSON');
  }

  if (!isRecord(parsed)) {
    throw new WorkingMemoryDraftError('INVALID_STRUCTURE');
  }

  if (!hasValidKnownFields(parsed)) {
    throw new WorkingMemoryDraftError('INVALID_FIELDS');
  }

  return parsed as Partial<WorkingMemoryV1>;
}

function readErrorCode(error: unknown) {
  if (!isRecord(error)) return null;
  return typeof error.code === 'string' ? error.code : null;
}

export function getWorkingMemorySaveErrorKey(error: unknown): WorkingMemorySaveErrorKey {
  if (error instanceof WorkingMemoryDraftError) {
    if (error.code === 'INVALID_JSON') {
      return 'settings.memoryGraph.advancedInvalidJsonError';
    }
    if (error.code === 'INVALID_STRUCTURE') {
      return 'settings.memoryGraph.advancedInvalidStructureError';
    }
    return 'settings.memoryGraph.advancedValidationError';
  }

  const code = readErrorCode(error);
  if (code === 'NETWORK_ERROR' || code === 'REQUEST_ABORTED') {
    return 'settings.memoryGraph.advancedNetworkError';
  }
  if (code === 'BAD_REQUEST' || code === 'VALIDATION_ERROR') {
    return 'settings.memoryGraph.advancedValidationError';
  }

  return 'settings.memoryGraph.advancedSaveError';
}
